import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect, Suspense, lazy } from "react";

import Header from "./Components/Header";
import Footer from "./Components/Footer";

// Geolocation Provider
import { GeoLocationProvider } from "./utils/useGeoLocation";

// Tawk.to initialization
import { initializeTawkTo } from "./utils/tawkto";

// Performance monitoring
import { initializePerformanceMonitoring } from "./utils/performanceMonitoring";

import "./css/about.css";
import "./css/mediaquery.css";

// Lazy load all page components for better code splitting
const Home = lazy(() => import("./Pages/Home"));
const Contact = lazy(() => import("./Pages/Contact"));
const Domain = lazy(() => import("./Pages/Domain"));
const IPAddress = lazy(() => import("./Pages/IPAddress"));
const DNSRecords = lazy(() => import("./Pages/DNSRecords"));
const Whois = lazy(() => import("./Pages/Whois"));
const VPSserver = lazy(() => import("./Pages/VPSserver"));
const Reseller = lazy(() => import("./Pages/Reseller"));
const Cloud = lazy(() => import("./Pages/Cloud"));
const SharedHosting = lazy(() => import("./Pages/SharedHosting"));
const FreeHosting = lazy(() => import("./Pages/FreeHosting"));
const CpanelHosting = lazy(() => import("./Pages/CpanelHosting"));
const PrivacyPolicy = lazy(() => import("./Company/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("./Company/TermsAndConditions"));
const FairUsagePolicy = lazy(() => import("./Company/FairUsagePolicy"));
const RefundPolicy = lazy(() => import("./Company/RefundPolicy"));
const FreeHostingPolicy = lazy(() => import("./Company/FreeHostingPolicy"));
const About = lazy(() => import("./Company/About"));
const Affiliate = lazy(() => import("./Company/Affiliate"));
const Unlimitedhostingpolicy = lazy(() => import("./Company/Unlimitedhostingpolicy"));
const Acceptusagepolicy = lazy(() => import("./Company/Acceptusagepolicy"));
const SLA = lazy(() => import("./Company/SLA"));
const NameServer = lazy(() => import("./Pages/NameServer"));
const Sitemap = lazy(() => import("./Pages/Sitemap"));
const Error = lazy(() => import("./Pages/Error"));
const Career = lazy(() => import("./Pages/Career"));
const PingTest = lazy(() => import("./Pages/PingTest"));
const Deals = lazy(() => import("./Pages/Deals"));
const BlogPage = lazy(() => import("./Blog/BlogPage"));
const BlogPostPage = lazy(() => import("./Blog/BlogPostPage"));

// Loading component for lazy loaded pages
const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
    <div>Loading...</div>
  </div>
);

function App() {
  useEffect(() => {
    // Global handlers to prevent uncaught third-party errors from breaking the app
    const onUnhandledRejection = (event) => {
      try {
        // Log and prevent default browser handling for third-party promise rejections
        const reason = event.reason;
        const stack = reason && reason.stack ? String(reason.stack) : '';
        const message = reason && reason.message ? String(reason.message) : '';

        // Suppress when stack or message references browser extensions known to interfere
        if (stack.includes('chrome-extension://') || message.includes('chrome-extension://') || stack.includes('frame_ant') || message.includes('frame_ant')) {
          console.warn('Suppressed unhandled rejection from extension:', message || stack);
          event.preventDefault();
          return;
        }

        console.warn('Unhandled promise rejection captured:', event.reason);
        // Let other unhandled rejections behave normally (do not swallow)
      } catch (e) {
        // ignore
      }
    };

    const onError = (event) => {
      try {
        const src = event.filename || '';
        const msg = event.message || '';
        const err = event.error || {};
        const stack = err.stack || '';

        // Suppress errors originating inside browser extensions or where the stack references them
        if (
          src.startsWith('chrome-extension://') ||
          src.includes('embed.tawk.to') ||
          stack.includes('chrome-extension://') ||
          msg.includes('chrome-extension://') ||
          stack.includes('frame_ant') ||
          msg.includes('frame_ant')
        ) {
          console.warn('Suppressed third-party error:', msg, src);
          event.preventDefault();
        }
      } catch (e) {
        // ignore
      }
    };

    window.addEventListener('unhandledrejection', onUnhandledRejection);
    window.addEventListener('error', onError);

    // Initialize Tawk.to when the app loads
    initializeTawkTo();

    // Initialize performance monitoring
    initializePerformanceMonitoring();

    // Cleanup handlers on unmount
    return () => {
      window.removeEventListener('unhandledrejection', onUnhandledRejection);
      window.removeEventListener('error', onError);
    };
  }, []);

  return (
    <>
      <GeoLocationProvider>
        <BrowserRouter>
          <Header />
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="*" element={<Error />} />
              <Route path="/" element={<Home />} />
              <Route
                path="/affordable-cpanel-hosting"
                element={<CpanelHosting />}
              />
              <Route path="/free-hosting" element={<FreeHosting />} />
              <Route path="/sharedHosting" element={<SharedHosting />} />
              <Route path="/cloud-based-hosting-services" element={<Cloud />} />
              <Route
                path="/affordable-reseller-web-hosting"
                element={<Reseller />}
              />
              <Route path="/affordable-linux-vps-hosting" element={<VPSserver />} />
              <Route path="/domain" element={<Domain />} />
              <Route path="/ip-address" element={<IPAddress />} />
              <Route path="/dns-records" element={<DNSRecords />} />
              <Route path="/whois" element={<Whois />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/nameserver" element={<NameServer />} />
              <Route path="/sitemap" element={<Sitemap />} />
              <Route path="/privacypolicy" element={<PrivacyPolicy />} />
              <Route
                path="/terms-and-conditions"
                element={<TermsAndConditions />}
              />
              <Route path="/fair-usage-policy" element={<FairUsagePolicy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/free-hosting-policy" element={<FreeHostingPolicy />} />
              <Route path="/about" element={<About />} />
              <Route path="/affiliate" element={<Affiliate />} />
              <Route path="/accept-usage-policy" element={<Acceptusagepolicy />} />
              <Route
                path="/unlimited-hosting-policy"
                element={<Unlimitedhostingpolicy />}
              />
              <Route path="/sla" element={<SLA />} />
              <Route path="/career" element={<Career />} />
              <Route path="/ping-test" element={<PingTest />} />
              <Route path="/deals" element={<Deals />} />
              <Route path="/blog" element={<BlogPage />} />
              <Route path="/blog/:slug" element={<BlogPostPage />} />
            </Routes>
          </Suspense>
          <Footer />
        </BrowserRouter>
      </GeoLocationProvider>
    </>
  );
}

export default App;
